/**
 * FeedbackModal — in-app feedback / bug-report modal.
 *
 * Track 4.Q — in-app feedback pipeline.
 * Feature plan: .plan/features/2026-04-24-admin-beta-feedback.md §2, §4.4
 *
 * Two entry points open this modal:
 *   1. BugIcon in the TopBar right cluster.
 *   2. "Send feedback" in the Settings / profile menu.
 *
 * Both entry points submit identically (same component, same RPC call).
 *
 * Voice compliance (branding.md §5, §5.2.1, §5.5):
 *   - Toast: "Logged." — no exclamation, sentence-final period, no `we`/`us`/`our`.
 *   - Labels: direct, no exclamation, no banned words.
 *   - Error copy: "Could not send. Try again." — precise, unsentimental.
 *   - No `we`/`us`/`our` on any user-visible string.
 *   - No `Instrument` aesthetic name surfaced anywhere.
 *
 * Accessibility (Wave 3 Track B — P0-15):
 *   - Built on Radix Dialog (Dialog.Root / Overlay / Content) so the focus
 *     trap, Tab-cycle, focus-restore, Escape, and aria-modal are solved at the
 *     primitive layer rather than hand-rolled. Radix's default auto-focus is
 *     suppressed via onOpenAutoFocus and we focus the first radio explicitly —
 *     so there is exactly ONE initial-focus mechanism (no double-focus race).
 *   - Role="dialog", aria-modal="true", aria-labelledby pointing to heading.
 *   - Backdrop click closes (Radix onPointerDownOutside → onClose).
 *
 * Design:
 *   - Matches Workspace chrome language (hairline borders, var(--bg), Geist Mono
 *     for labels). No accent CTA inside the modal — submit uses secondary styling.
 *   - Max-width 480px, centred. Scrollable on mobile.
 *   - Rule 7: works on 375px.
 */

import { useState, useEffect, useRef, type FormEvent } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { ScreenshotAttachment } from "./ScreenshotAttachment.js";

// ── Types ─────────────────────────────────────────────────────────────────────

export type FeedbackKind = "bug" | "feedback";

export interface FeedbackModalProps {
  /** Whether the modal is open. */
  open: boolean;
  /** Called to close the modal. */
  onClose: () => void;
  /**
   * Submit callback. Called with the validated form data.
   * Should call feedback.submit on the server.
   * Returns { ok: true } on success, throws on failure.
   */
  onSubmit: (data: {
    kind: FeedbackKind;
    body: string;
    screenshotR2Key: string | null;
    metadata: {
      url: string;
      ua: string;
      app_version: string;
      viewport: string;
      locale: string;
    };
  }) => Promise<void>;
  /**
   * Called to request a presigned screenshot upload URL.
   * Returns { putUrl, r2Key } from feedback.presignScreenshotUpload.
   */
  onRequestPresignUrl: () => Promise<{ putUrl: string; r2Key: string }>;
  /**
   * Called to display a toast message in the parent Workspace.
   * Used to show "Logged." after successful submit.
   */
  onToast?: (message: string) => void;
}

// ── CSS ───────────────────────────────────────────────────────────────────────

const FEEDBACK_MODAL_CSS = `
  .proof-feedback-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.32);
    z-index: 900;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--space-4);
    box-sizing: border-box;
  }

  .proof-feedback-modal {
    background: var(--bg);
    border: 1px solid var(--line);
    border-radius: var(--radius-card);
    width: 100%;
    max-width: 480px;
    max-height: 90dvh;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
  }

  .proof-feedback-modal__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--space-4) var(--space-5);
    border-bottom: 1px solid var(--line);
    flex-shrink: 0;
  }

  .proof-feedback-modal__title {
    font-family: var(--font-sans);
    font-size: 15px;
    font-weight: 500;
    color: var(--text);
    margin: 0;
  }

  .proof-feedback-modal__close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    background: none;
    border: none;
    cursor: pointer;
    color: var(--muted);
    font-family: var(--font-sans);
    font-size: 16px;
    border-radius: var(--radius-button);
    transition: color var(--motion-state);
    -webkit-tap-highlight-color: transparent;
    flex-shrink: 0;
  }

  .proof-feedback-modal__close:hover {
    color: var(--text);
  }

  .proof-feedback-modal__body {
    padding: var(--space-5);
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
  }

  /* Kind radio group */
  .proof-feedback-kind {
    display: flex;
    gap: var(--space-3);
  }

  .proof-feedback-kind__label {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-family: var(--font-sans);
    font-size: 13px;
    color: var(--text);
    cursor: pointer;
  }

  .proof-feedback-kind__label input[type="radio"] {
    accent-color: var(--accent);
    width: 16px;
    height: 16px;
    cursor: pointer;
    flex-shrink: 0;
  }

  /* Textarea */
  .proof-feedback-textarea {
    width: 100%;
    min-height: 120px;
    resize: vertical;
    background: var(--surface);
    border: 1px solid var(--line);
    border-radius: var(--radius-button);
    padding: var(--space-3);
    font-family: var(--font-sans);
    font-size: 13px;
    color: var(--text);
    line-height: 1.5;
    box-sizing: border-box;
    transition: border-color var(--motion-state);
    -webkit-appearance: none;
  }

  .proof-feedback-textarea:focus {
    outline: none;
    border-color: var(--accent);
  }

  .proof-feedback-textarea::placeholder {
    color: var(--faint);
  }

  /* Character count */
  .proof-feedback-charcount {
    font-family: var(--font-mono);
    font-size: 11px;
    color: var(--muted);
    text-align: right;
    margin-top: calc(-1 * var(--space-3));
  }

  .proof-feedback-charcount.over {
    color: var(--destructive, #e53e3e);
  }

  /* Screenshot section label */
  .proof-feedback-section-label {
    font-family: var(--font-mono);
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--muted);
    margin-bottom: var(--space-2);
    display: block;
  }

  /* Screenshot attachment */
  .proof-feedback-screenshot__label {
    display: inline-flex;
  }

  .proof-feedback-screenshot__btn {
    font-family: var(--font-sans);
    font-size: 13px;
    color: var(--muted);
    border: 1px solid var(--line);
    border-radius: var(--radius-button);
    padding: var(--space-2) var(--space-3);
    cursor: pointer;
    transition: color var(--motion-state), border-color var(--motion-state);
    -webkit-tap-highlight-color: transparent;
  }

  .proof-feedback-screenshot__btn:hover {
    color: var(--text);
    border-color: color-mix(in srgb, var(--text) 20%, var(--line));
  }

  .proof-feedback-screenshot__uploading {
    font-family: var(--font-sans);
    font-size: 13px;
    color: var(--muted);
  }

  .proof-feedback-screenshot__attached {
    display: flex;
    align-items: center;
    gap: var(--space-3);
  }

  .proof-feedback-screenshot__filename {
    font-family: var(--font-mono);
    font-size: 12px;
    color: var(--text);
    max-width: 240px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .proof-feedback-screenshot__remove {
    font-family: var(--font-sans);
    font-size: 12px;
    color: var(--muted);
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    text-decoration: underline;
    text-underline-offset: 2px;
    transition: color var(--motion-state);
    -webkit-tap-highlight-color: transparent;
  }

  .proof-feedback-screenshot__remove:hover {
    color: var(--text);
  }

  .proof-feedback-screenshot__error {
    font-family: var(--font-sans);
    font-size: 12px;
    color: var(--destructive, #e53e3e);
    margin: var(--space-2) 0 0;
  }

  /* Footer */
  .proof-feedback-modal__footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: var(--space-3);
    padding: var(--space-4) var(--space-5);
    border-top: 1px solid var(--line);
    flex-shrink: 0;
  }

  .proof-feedback-cancel {
    font-family: var(--font-sans);
    font-size: 13px;
    color: var(--muted);
    background: none;
    border: 1px solid var(--line);
    border-radius: var(--radius-button);
    padding: 0 var(--space-4);
    height: 36px;
    min-height: var(--tap-target-min, 44px);
    cursor: pointer;
    transition: color var(--motion-state), border-color var(--motion-state);
    -webkit-tap-highlight-color: transparent;
  }

  .proof-feedback-cancel:hover {
    color: var(--text);
  }

  .proof-feedback-submit {
    font-family: var(--font-sans);
    font-size: 13px;
    font-weight: 500;
    color: var(--text);
    background: var(--surface);
    border: 1px solid var(--line);
    border-radius: var(--radius-button);
    padding: 0 var(--space-5);
    height: 36px;
    min-height: var(--tap-target-min, 44px);
    cursor: pointer;
    transition: background var(--motion-state), border-color var(--motion-state);
    -webkit-tap-highlight-color: transparent;
    white-space: nowrap;
  }

  .proof-feedback-submit:hover:not(:disabled) {
    background: color-mix(in srgb, var(--text) 6%, var(--surface));
  }

  .proof-feedback-submit:disabled {
    /* Explicit disabled colors replace opacity (which faded text + border below
       legible). Outlined button: --cta-disabled-fg text (4.0:1 on the disabled
       fill), --cta-disabled-bg fill, --line-strong border so the control's outer
       edge stays perceivable (3:1 on --bg). */
    background: var(--cta-disabled-bg);
    color: var(--cta-disabled-fg);
    border-color: var(--line-strong);
    cursor: default;
  }

  .proof-feedback-error-banner {
    font-family: var(--font-sans);
    font-size: 13px;
    color: var(--destructive, #e53e3e);
    padding: var(--space-2) var(--space-5);
    border-top: 1px solid var(--line);
  }

  @media (max-width: 480px) {
    .proof-feedback-modal {
      border-radius: var(--radius-card) var(--radius-card) 0 0;
    }
    .proof-feedback-backdrop {
      align-items: flex-end;
      padding: 0;
    }
  }
`;

// ── Body cap ──────────────────────────────────────────────────────────────────

const BODY_MAX_CHARS = 4096;

// ── Component ─────────────────────────────────────────────────────────────────

export function FeedbackModal({
  open,
  onClose,
  onSubmit,
  onRequestPresignUrl,
  onToast,
}: FeedbackModalProps) {
  const [kind, setKind] = useState<FeedbackKind>("bug");
  const [body, setBody] = useState("");
  const [screenshotR2Key, setScreenshotR2Key] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const firstFocusRef = useRef<HTMLInputElement>(null);

  // Inject CSS once
  useEffect(() => {
    const id = "proof-feedback-modal-css";
    if (typeof document !== "undefined" && document.getElementById(id) === null) {
      const el = document.createElement("style");
      el.id = id;
      el.textContent = FEEDBACK_MODAL_CSS;
      document.head.appendChild(el);
    }
  }, []);

  // P0-15: initial focus + Escape are now handled by Radix Dialog. The
  // hand-rolled focus useEffect + window keydown listener were removed to avoid
  // a double-focus race (Radix autofocus + manual focus firing together).
  // Initial focus is set ONCE via Dialog.Content's onOpenAutoFocus below.

  // Reset form on close
  useEffect(() => {
    if (!open) {
      setBody("");
      setKind("bug");
      setScreenshotR2Key(null);
      setSubmitting(false);
      setSubmitError(null);
    }
  }, [open]);

  // P0-15: Radix Dialog.Root gates mount/unmount on the `open` prop (renders
  // nothing when closed), so an explicit early return is no longer needed. The
  // hooks above still run on every render to reset form state on close.

  const bodyTrimmed = body.trim();
  const bodyOverLimit = body.length > BODY_MAX_CHARS;
  const canSubmit = bodyTrimmed.length > 0 && !bodyOverLimit && !submitting;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    setSubmitError(null);

    try {
      await onSubmit({
        kind,
        body: bodyTrimmed,
        screenshotR2Key,
        metadata: {
          url: typeof window !== "undefined" ? window.location.pathname : "",
          ua: typeof navigator !== "undefined" ? navigator.userAgent : "",
          // app_version is required by FeedbackMetadataSchema (.strict()).
          // Vite replaces import.meta.env.VITE_APP_VERSION at build time
          // (vite.config.ts define block). Falls back to "dev" in vitest
          // and any environment where the env var was not set.
          app_version: (import.meta.env["VITE_APP_VERSION"] as string | undefined) ?? "dev",
          viewport:
            typeof window !== "undefined" ? `${window.innerWidth}x${window.innerHeight}` : "",
          locale: typeof navigator !== "undefined" ? (navigator.language ?? "") : "",
        },
      });
      onToast?.("Logged.");
      onClose();
    } catch (err) {
      // Log to console so the failure is never silently swallowed.
      console.error({ event: "feedback_submit_failed", err });
      setSubmitError("Could not send. Try again.");
      setSubmitting(false);
    }
  }

  const charsLeft = BODY_MAX_CHARS - body.length;

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <Dialog.Portal>
        {/*
         * Backdrop click closes. Radix dismisses on pointer-down-outside the
         * Content; we also keep an explicit onClick on the Overlay so a plain
         * click on the scrim closes (preserves prior behaviour + the
         * backdrop-click test).
         */}
        <Dialog.Overlay
          className="proof-feedback-backdrop"
          onClick={(e) => {
            if (e.target === e.currentTarget) onClose();
          }}
        >
          <Dialog.Content
            className="proof-feedback-modal"
            aria-labelledby="feedback-modal-title"
            aria-describedby={undefined}
            data-testid="feedback-modal"
            /*
             * P0-15 — single initial-focus mechanism. Suppress Radix's default
             * auto-focus and focus the first radio ourselves so initial focus
             * never fires twice. Radix still owns the trap, Tab-cycle, ESC,
             * focus-restore, and aria-modal.
             */
            onOpenAutoFocus={(e) => {
              e.preventDefault();
              firstFocusRef.current?.focus();
            }}
          >
            {/* Header */}
            <div className="proof-feedback-modal__header">
              <h2 className="proof-feedback-modal__title" id="feedback-modal-title">
                Send feedback
              </h2>
              <button
                type="button"
                className="proof-feedback-modal__close"
                onClick={onClose}
                aria-label="Close"
                data-testid="feedback-close-btn"
              >
                ×
              </button>
            </div>

            {/* Form body */}
            <form onSubmit={(e) => void handleSubmit(e)}>
              <div className="proof-feedback-modal__body">
                {/* Kind selector */}
                <div className="proof-feedback-kind" role="radiogroup" aria-label="Type">
                  <label className="proof-feedback-kind__label">
                    <input
                      ref={firstFocusRef}
                      type="radio"
                      name="feedback-kind"
                      value="bug"
                      checked={kind === "bug"}
                      onChange={() => setKind("bug")}
                      data-testid="kind-bug"
                    />
                    Bug
                  </label>
                  <label className="proof-feedback-kind__label">
                    <input
                      type="radio"
                      name="feedback-kind"
                      value="feedback"
                      checked={kind === "feedback"}
                      onChange={() => setKind("feedback")}
                      data-testid="kind-feedback"
                    />
                    Feedback
                  </label>
                </div>

                {/* Message textarea */}
                <div>
                  <textarea
                    className="proof-feedback-textarea"
                    placeholder={kind === "bug" ? "Describe what happened." : "Share a thought."}
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    maxLength={BODY_MAX_CHARS + 1} // allow one over to trigger visual warning
                    aria-label="Message"
                    aria-required="true"
                    disabled={submitting}
                    data-testid="feedback-body"
                  />
                  {body.length > BODY_MAX_CHARS - 200 && (
                    <p
                      className={`proof-feedback-charcount${bodyOverLimit ? " over" : ""}`}
                      aria-live="polite"
                    >
                      {bodyOverLimit
                        ? `${Math.abs(charsLeft)} over limit`
                        : `${charsLeft} remaining`}
                    </p>
                  )}
                </div>

                {/* Optional screenshot */}
                <div>
                  <span className="proof-feedback-section-label">Screenshot</span>
                  <ScreenshotAttachment
                    onAttach={setScreenshotR2Key}
                    disabled={submitting}
                    onRequestPresignUrl={onRequestPresignUrl}
                  />
                </div>
              </div>

              {/* Error banner */}
              {submitError !== null && (
                <p className="proof-feedback-error-banner" role="alert" data-testid="submit-error">
                  {submitError}
                </p>
              )}

              {/* Footer */}
              <div className="proof-feedback-modal__footer">
                <button
                  type="button"
                  className="proof-feedback-cancel"
                  onClick={onClose}
                  disabled={submitting}
                  data-testid="feedback-cancel-btn"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="proof-feedback-submit"
                  disabled={!canSubmit}
                  data-testid="feedback-submit-btn"
                >
                  {submitting ? "Sending…" : "Send"}
                </button>
              </div>
            </form>
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
